import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, ReferenceLine, Legend, ComposedChart, Line, Area } from 'recharts';
import { ArrowRightLeft, Activity, AlertTriangle, TrendingUp, Map, Zap, ArrowRight, Maximize2, Info } from 'lucide-react';
// Mock Data
const flowData = [{
  link: 'FR-DE',
  capacity: 3000,
  flow: 2450,
  scheduled: 2400,
  priceDiff: 12.5,
  congestion: false
}, {
  link: 'DE-NL',
  capacity: 2500,
  flow: 2100,
  scheduled: 2150,
  priceDiff: 4.2,
  congestion: false
}, {
  link: 'NL-BE',
  capacity: 1800,
  flow: 1750,
  scheduled: 1800,
  priceDiff: 18.4,
  congestion: true
}, {
  link: 'BE-FR',
  capacity: 2200,
  flow: -1200,
  scheduled: -1150,
  priceDiff: -8.5,
  congestion: false
}, {
  link: 'FR-ES',
  capacity: 2800,
  flow: 2800,
  scheduled: 2800,
  priceDiff: 24.1,
  congestion: true
}, {
  link: 'ES-PT',
  capacity: 1500,
  flow: 800,
  scheduled: 800,
  priceDiff: 2.1,
  congestion: false
}, {
  link: 'DE-DK',
  capacity: 1200,
  flow: 1100,
  scheduled: 1100,
  priceDiff: 15.2,
  congestion: true
}, {
  link: 'NO-DE',
  capacity: 1400,
  flow: 1350,
  scheduled: 1350,
  priceDiff: 10.8,
  congestion: false
}];
const hourlyFlows = Array.from({
  length: 24
}, (_, i) => ({
  hour: `${i}:00`,
  import: Math.random() * 2000 + 1000,
  export: Math.random() * 1500 + 500,
  net: 0,
  price: Math.random() * 40 + 40
})).map(d => ({
  ...d,
  net: d.import - d.export
}));
const CustomTooltip = ({
  active,
  payload,
  label
}: any) => {
  if (active && payload && payload.length) {
    return <div className="bg-white/95 backdrop-blur-xl border border-slate-200 rounded-xl p-4 shadow-xl z-50" data-id="element-1274">
        <p className="text-xs font-bold text-slate-900 mb-2" data-id="element-1275">{label}</p>
        <div className="space-y-1" data-id="element-1276">
          {payload.map((entry: any) => <div key={entry.name} className="flex items-center justify-between gap-4" data-id="element-1277">
              <span className="text-xs text-slate-600 capitalize" data-id="element-1278">
                {entry.name}:
              </span>
              <span className="text-xs font-mono font-medium text-slate-900" data-id="element-1279">
                {typeof entry.value === 'number' ? entry.value.toFixed(1) : entry.value}
                {entry.unit || ''}
              </span>
            </div>)}
        </div>
      </div>;
  }
  return null;
};
export function CrossBorderFlowDashboard() {
  return <div className="min-h-screen p-6 space-y-6 pb-20 bg-slate-50/50" data-id="element-1280">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-1281">
        <div data-id="element-1282">
          <div className="flex items-center gap-2 mb-1" data-id="element-1283">
            <div className="p-2 bg-blue-500 rounded-lg" data-id="element-1284">
              <ArrowRightLeft size={20} className="text-white" data-id="element-1285" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight" data-id="element-1286">
              Cross-Border Flows
            </h1>
          </div>
          <p className="text-slate-500 text-sm md:text-base ml-11" data-id="element-1287">
            Interconnector utilization, congestion analysis, and arbitrage
            opportunities
          </p>
        </div>

        <div className="flex gap-2" data-id="element-1288">
          <span className="px-3 py-1 bg-red-50 text-red-600 rounded-full text-xs font-medium border border-red-100 flex items-center gap-1" data-id="element-1289">
            <AlertTriangle size={12} data-id="element-1290" /> 3 Congested Lines
          </span>
          <span className="px-3 py-1 bg-emerald-50 text-emerald-600 rounded-full text-xs font-medium border border-emerald-100 flex items-center gap-1" data-id="element-1291">
            <Zap size={12} data-id="element-1292" /> €14.2k Arbitrage Potential
          </span>
        </div>
      </div>

      {/* Main Flow Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-1293">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="lg:col-span-2 bg-white/60 backdrop-blur-xl border border-slate-200/60 rounded-xl p-6 shadow-sm" data-id="element-1294">
          <div className="flex justify-between items-center mb-6" data-id="element-1295">
            <h3 className="text-lg font-bold text-slate-900" data-id="element-1296">
              Interconnector Utilization
            </h3>
            <div className="flex gap-2 text-xs" data-id="element-1297">
              <span className="flex items-center gap-1" data-id="element-1298">
                <div className="w-2 h-2 rounded-full bg-blue-500" data-id="element-1299"></div> Flow
              </span>
              <span className="flex items-center gap-1" data-id="element-1300">
                <div className="w-2 h-2 rounded-full bg-slate-200" data-id="element-1301"></div>{' '}
                Capacity
              </span>
            </div>
          </div>

          <div className="h-[350px] w-full" data-id="element-1302">
            <ResponsiveContainer width="100%" height="100%" data-id="element-1303">
              <BarChart layout="vertical" data={flowData} margin={{
              top: 5,
              right: 30,
              left: 40,
              bottom: 5
            }} data-id="element-1304">
                <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} stroke="#e2e8f0" data-id="element-1305" />
                <XAxis type="number" hide data-id="element-1306" />
                <YAxis dataKey="link" type="category" axisLine={false} tickLine={false} tick={{
                fontSize: 12,
                fontWeight: 600,
                fill: '#475569'
              }} width={60} data-id="element-1307" />
                <Tooltip content={<CustomTooltip data-id="element-1309" />} data-id="element-1308" />
                <Bar dataKey="capacity" fill="#cbd5e1" radius={[0, 4, 4, 0]} barSize={20} fillOpacity={0.4} data-id="element-1310" />
                <Bar dataKey="flow" fill="#3b82f6" radius={[0, 4, 4, 0]} barSize={20} data-id="element-1311">
                  {flowData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.congestion ? '#ef4444' : '#3b82f6'} data-id="element-1312" />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        <div className="space-y-6" data-id="element-1313">
          {/* Arbitrage Opportunities */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="bg-white/60 backdrop-blur-xl border border-slate-200/60 rounded-xl p-6 shadow-sm h-full" data-id="element-1314">
            <div className="flex items-start justify-between mb-4" data-id="element-1315">
              <h3 className="text-lg font-bold text-slate-900" data-id="element-1316">
                Top Arbitrage Spreads
              </h3>
              <div className="relative group" data-id="element-1317">
                <Info size={18} className="text-slate-400 hover:text-slate-600 cursor-help transition-colors" data-id="element-1318" />
                <div className="absolute right-0 top-full mt-2 w-72 bg-white border border-slate-200 rounded-xl shadow-xl p-4 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50" data-id="element-1319">
                  <h4 className="font-bold text-sm text-slate-900 mb-2" data-id="element-1320">
                    Arbitrage Opportunities
                  </h4>
                  <p className="text-xs text-slate-600 mb-3" data-id="element-1321">
                    Price differences between interconnected markets create
                    trading opportunities. Buy low in one market, sell high in
                    another.
                  </p>
                  <div className="space-y-2 text-xs" data-id="element-1322">
                    <div className="flex items-start gap-2" data-id="element-1323">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1 flex-shrink-0" data-id="element-1324"></div>
                      <div data-id="element-1325">
                        <span className="font-medium text-slate-700" data-id="element-1326">
                          Spread:
                        </span>
                        <span className="text-slate-600" data-id="element-1327">
                          {' '}
                          Price difference in €/MWh
                        </span>
                      </div>
                    </div>
                    <div className="flex items-start gap-2" data-id="element-1328">
                      <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1 flex-shrink-0" data-id="element-1329"></div>
                      <div data-id="element-1330">
                        <span className="font-medium text-slate-700" data-id="element-1331">
                          Flow:
                        </span>
                        <span className="text-slate-600" data-id="element-1332">
                          {' '}
                          Current power transfer
                        </span>
                      </div>
                    </div>
                    <div className="flex items-start gap-2" data-id="element-1333">
                      <div className="w-1.5 h-1.5 rounded-full bg-red-500 mt-1 flex-shrink-0" data-id="element-1334"></div>
                      <div data-id="element-1335">
                        <span className="font-medium text-slate-700" data-id="element-1336">
                          Red bar:
                        </span>
                        <span className="text-slate-600" data-id="element-1337">
                          {' '}
                          Congested (at capacity limit)
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="space-y-4" data-id="element-1338">
              {flowData.filter(d => Math.abs(d.priceDiff) > 5).sort((a, b) => Math.abs(b.priceDiff) - Math.abs(a.priceDiff)).slice(0, 4).map((item, idx) => <div key={idx} className="p-3 rounded-lg bg-white border border-slate-100 hover:shadow-md transition-shadow" data-id="element-1339">
                    <div className="flex justify-between items-center mb-2" data-id="element-1340">
                      <span className="font-bold text-slate-700" data-id="element-1341">
                        {item.link}
                      </span>
                      <span className="text-emerald-600 font-bold bg-emerald-50 px-2 py-0.5 rounded text-xs" data-id="element-1342">
                        €{Math.abs(item.priceDiff).toFixed(2)}/MWh
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-500" data-id="element-1343">
                      <span data-id="element-1344">Flow: {item.flow} MW</span>
                      <ArrowRight size={12} data-id="element-1345" />
                      <span data-id="element-1346">Cap: {item.capacity} MW</span>
                    </div>
                    <div className="mt-2 w-full bg-slate-100 rounded-full h-1.5" data-id="element-1347">
                      <div className={`h-1.5 rounded-full ${item.congestion ? 'bg-red-500' : 'bg-blue-500'}`} style={{
                  width: `${Math.abs(item.flow / item.capacity) * 100}%`
                }} data-id="element-1348"></div>
                    </div>
                  </div>)}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Net Position Chart */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.2
    }} className="bg-white/60 backdrop-blur-xl border border-slate-200/60 rounded-xl p-6 shadow-sm" data-id="element-1349">
        <div className="flex justify-between items-center mb-6" data-id="element-1350">
          <div data-id="element-1351">
            <h3 className="text-lg font-bold text-slate-900" data-id="element-1352">
              Net Position Forecast
            </h3>
            <p className="text-sm text-slate-500" data-id="element-1353">
              Aggregated import/export balance (MW)
            </p>
          </div>
          <div className="flex gap-4 text-sm" data-id="element-1354">
            <div className="flex items-center gap-2" data-id="element-1355">
              <div className="w-3 h-3 bg-emerald-500 rounded-full" data-id="element-1356"></div>
              <span className="text-slate-600" data-id="element-1357">Net Position</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-1358">
              <div className="w-3 h-3 bg-slate-400 rounded-full" data-id="element-1359"></div>
              <span className="text-slate-600" data-id="element-1360">Price</span>
            </div>
          </div>
        </div>

        <div className="h-[300px] w-full" data-id="element-1361">
          <ResponsiveContainer width="100%" height="100%" data-id="element-1362">
            <ComposedChart data={hourlyFlows} margin={{
            top: 10,
            right: 10,
            left: 0,
            bottom: 0
          }} data-id="element-1363">
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" data-id="element-1364" />
              <XAxis dataKey="hour" axisLine={false} tickLine={false} tick={{
              fontSize: 12,
              fill: '#64748b'
            }} data-id="element-1365" />
              <YAxis yAxisId="left" axisLine={false} tickLine={false} tick={{
              fontSize: 12,
              fill: '#64748b'
            }} data-id="element-1366" />
              <YAxis yAxisId="right" orientation="right" axisLine={false} tickLine={false} tick={{
              fontSize: 12,
              fill: '#64748b'
            }} data-id="element-1367" />
              <Tooltip content={<CustomTooltip data-id="element-1369" />} data-id="element-1368" />
              <ReferenceLine y={0} yAxisId="left" stroke="#94a3b8" data-id="element-1370" />
              <Area yAxisId="left" type="monotone" dataKey="net" fill="url(#colorNet)" stroke="#10b981" fillOpacity={0.1} data-id="element-1371" />
              <Line yAxisId="right" type="monotone" dataKey="price" stroke="#64748b" strokeWidth={2} dot={false} data-id="element-1372" />
              <defs data-id="element-1373">
                <linearGradient id="colorNet" x1="0" y1="0" x2="0" y2="1" data-id="element-1374">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} data-id="element-1375" />
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0} data-id="element-1376" />
                </linearGradient>
              </defs>
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </motion.div>
    </div>;
}